import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import Spinner from './Spinner';

// ACC.A2 follow-up (#848) — minimal shape of the dev_reflection
// content that the Generate Code gate cares about. The DevReflectionCard
// renders the full JSONB; here we only need the selfAssessment plus the
// two flagged-item lists for the rework-confirm modal.
export interface ImplementationPromptReflectionGate {
  selfAssessment: 'READY_FOR_REVIEW' | 'NEEDS_REWORK';
  shortcutsIdentified: string[];
  scopeAdditions: string[];
}

interface ImplementationPromptCardProps {
  artefact: {
    id: string;
    title: string;
    content: string;
  };
  sessionId: string;
  projectId: string;
  generating?: boolean;
  // ACC.A2 follow-up (#848) — when the latest dev_reflection has
  // selfAssessment === 'NEEDS_REWORK', the Generate Code button opens
  // a confirm modal surfacing the shortcuts + scope additions before
  // proceeding. null/undefined means no reflection yet (the orchestrator
  // is still running, or pre-A2 sessions) → behaves as before, no gate.
  reflection?: ImplementationPromptReflectionGate | null;
  onGenerateCode: () => void;
  onRequestChanges: (prefill: string) => void;
}

export default function ImplementationPromptCard({
  artefact,
  projectId,
  generating,
  reflection,
  onGenerateCode,
  onRequestChanges,
}: ImplementationPromptCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [showReworkConfirm, setShowReworkConfirm] = useState(false);

  const needsRework = reflection?.selfAssessment === 'NEEDS_REWORK';

  // ── GH.A2 (#942) — feature branch state ───────────────────────────────────
  // Self-fetches status + branchRef so we can surface the badge without
  // requiring ChatPage to thread these props through (the existing
  // promptCard state is { id, title, content } and adding two fields
  // would ripple through every restorePromptCard + SSE handler).
  //
  // After approval flips, polls /artefacts/:id every 10s up to 5 times
  // (50s grace window — background creation typically completes in
  // 1–3s). If the cap is reached without branchRef appearing, shows
  // amber "Branch not created" + Retry button. The retry endpoint is
  // idempotent (service fast-paths when branchRef is already pinned),
  // so racing against a background-just-finished call is safe.
  const [artefactStatus, setArtefactStatus] = useState<string | null>(null);
  const [branchRef, setBranchRef] = useState<string | null>(null);
  const [branchPollExhausted, setBranchPollExhausted] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [branchError, setBranchError] = useState('');

  // ── CRA.A2 (#946) — PR state ──────────────────────────────────────────────
  // PR is created on approval AFTER the branch exists. Same self-fetch +
  // poll + retry pattern as the branch surface above. pr_url/pr_number are
  // returned by GET /artefacts/:id (the detail endpoint spreads ...artefact).
  const [prUrl, setPrUrl] = useState<string | null>(null);
  const [prNumber, setPrNumber] = useState<number | null>(null);
  const [prPollExhausted, setPrPollExhausted] = useState(false);
  const [retryingPr, setRetryingPr] = useState(false);
  const [prError, setPrError] = useState('');

  // #1037 — Approve action state (mirrors ArtefactCard.handleApprove).
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState('');

  // AUTO.1 Part 3 — manual Execute button state. Thin caller: the server
  // resolves issueId/issueTitle/branchName via the same seam adapter the
  // auto-trigger uses (POST .../execute/trigger takes only artefactId), and
  // enforces the SAME fail-closed flag gate as every other execution caller
  // — this button cannot arm or bypass it, it can only ask.
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState('');
  const [executeSessionId, setExecuteSessionId] = useState<string | null>(null);

  // Initial fetch of status + branchRef + PR on mount / artefact change.
  useEffect(() => {
    let cancelled = false;
    apiFetch<{ status: string; branchRef: string | null; prUrl: string | null; prNumber: number | null }>(`/artefacts/${artefact.id}`)
      .then((data) => {
        if (cancelled) return;
        setArtefactStatus(data.status);
        setBranchRef(data.branchRef ?? null);
        setPrUrl(data.prUrl ?? null);
        setPrNumber(data.prNumber ?? null);
      })
      .catch(() => {
        // Best-effort — branch/PR surface stays hidden if the fetch fails.
      });
    return () => {
      cancelled = true;
    };
  }, [artefact.id]);

  // Poll when approved but no branchRef yet.
  useEffect(() => {
    if (artefactStatus !== 'approved') return;
    if (branchRef) return;
    if (branchPollExhausted) return;

    let pollCount = 0;
    const POLL_CAP = 5;
    const POLL_MS  = 10000;
    const timerId = setInterval(() => {
      pollCount += 1;
      apiFetch<{ branchRef?: string | null }>(`/artefacts/${artefact.id}`)
        .then((data) => {
          const ref = data.branchRef ?? null;
          if (ref) {
            setBranchRef(ref);
            clearInterval(timerId);
          } else if (pollCount >= POLL_CAP) {
            setBranchPollExhausted(true);
            clearInterval(timerId);
          }
        })
        .catch(() => {
          if (pollCount >= POLL_CAP) {
            setBranchPollExhausted(true);
            clearInterval(timerId);
          }
        });
    }, POLL_MS);

    return () => clearInterval(timerId);
  }, [artefact.id, artefactStatus, branchRef, branchPollExhausted]);

  const handleRetryBranch = async () => {
    setRetrying(true);
    setBranchError('');
    try {
      const result = await apiFetch<{
        ok: boolean;
        branchName?: string;
        reason?: string;
        error?: string;
      }>(`/artefacts/${artefact.id}/retry-branch`, { method: 'POST' });
      if (result.ok && result.branchName) {
        setBranchRef(result.branchName);
        setBranchPollExhausted(false);
      } else {
        setBranchError(result.error ?? result.reason ?? 'Branch creation failed');
      }
    } catch (err) {
      setBranchError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  // CRA.A2 — poll for pr_url once a branch exists but no PR yet (PR creation
  // chains after branch creation, so it lands a beat later). Same 5×10s grace
  // window as the branch poll.
  useEffect(() => {
    if (artefactStatus !== 'approved') return;
    if (!branchRef) return;           // no branch ⇒ no PR is even attempted
    if (prUrl) return;
    if (prPollExhausted) return;

    let pollCount = 0;
    const POLL_CAP = 5;
    const POLL_MS  = 10000;
    const timerId = setInterval(() => {
      pollCount += 1;
      apiFetch<{ prUrl?: string | null; prNumber?: number | null }>(`/artefacts/${artefact.id}`)
        .then((data) => {
          if (data.prUrl) {
            setPrUrl(data.prUrl);
            setPrNumber(data.prNumber ?? null);
            clearInterval(timerId);
          } else if (pollCount >= POLL_CAP) {
            setPrPollExhausted(true);
            clearInterval(timerId);
          }
        })
        .catch(() => {
          if (pollCount >= POLL_CAP) {
            setPrPollExhausted(true);
            clearInterval(timerId);
          }
        });
    }, POLL_MS);

    return () => clearInterval(timerId);
  }, [artefact.id, artefactStatus, branchRef, prUrl, prPollExhausted]);

  const handleRetryPr = async () => {
    setRetryingPr(true);
    setPrError('');
    try {
      const result = await apiFetch<{
        ok: boolean;
        prUrl?: string;
        prNumber?: number;
        reason?: string;
        error?: string;
      }>(`/artefacts/${artefact.id}/retry-pr`, { method: 'POST' });
      if (result.ok && result.prUrl) {
        setPrUrl(result.prUrl);
        setPrNumber(result.prNumber ?? null);
        setPrPollExhausted(false);
      } else {
        setPrError(result.error ?? result.reason ?? 'PR creation failed');
      }
    } catch (err) {
      setPrError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetryingPr(false);
    }
  };

  // Round 1 review (#848 MEDIUM): if reflection transitions away from
  // NEEDS_REWORK while the modal is open (or away from existing entirely),
  // close the modal so a stale state doesn't re-open it on the next
  // re-render. The render guard `showReworkConfirm && reflection` would
  // already collapse the modal visually, but the boolean staying `true`
  // would cause a re-open if reflection later returns to NEEDS_REWORK
  // without user action.
  //
  // setTimeout defers the setState off the synchronous effect tick to
  // satisfy the codebase's "no cascading renders in effect" lint rule
  // (same pattern used by DimensionProgress.tsx for justUnlocked).
  useEffect(() => {
    if (!needsRework) {
      const t = setTimeout(() => setShowReworkConfirm(false), 0);
      return () => clearTimeout(t);
    }
  }, [needsRework]);

  const previewLines = artefact.content.split('\n').slice(0, 3).join('\n');

  const handleExport = () => {
    const blob = new Blob([artefact.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${artefact.title.replace(/[^a-zA-Z0-9_-]/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // #1037 — Approve the implementation_prompt. This is the workflow-advance
  // action that fires GH.A2 (branch) + CRA.A2 (PR). Mirrors
  // ArtefactCard.handleApprove: PATCH /artefacts/:id/status → 'approved', then
  // set local artefactStatus so the existing branch_ref/pr_url poll effects
  // (keyed on artefactStatus) start automatically. Server enforces approval
  // authority (checkTierApprovalPermission + checkApprovalAuthority), so the
  // button is shown unconditionally and errors surface here. NEEDS_REWORK does
  // NOT block approval — that verdict gates the Phase 4→5 handoff, not this
  // transition (every prompt is NEEDS_REWORK today per #1036).
  const handleApprove = async () => {
    setApproving(true);
    setApproveError('');
    try {
      await apiFetch(`/artefacts/${artefact.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'approved' }),
      });
      setArtefactStatus('approved');
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };

  const handleGenerateClick = () => {
    if (needsRework) {
      setShowReworkConfirm(true);
      return;
    }
    onGenerateCode();
  };

  // AUTO.1 Part 3 — thin caller: the server resolves issueId/issueTitle/
  // branchName and enforces the fail-closed execution-flag gate; this
  // button only asks. A 503 EXECUTION_AGENT_DISABLED (the common case today
  // — execution is armed for zero tenants in production) surfaces as a
  // plain error message, same as any other rejected action on this card.
  const handleExecute = async () => {
    setExecuting(true);
    setExecuteError('');
    try {
      const result = await apiFetch<{ sessionId: string; message: string }>(
        `/projects/${projectId}/phases/phase_4_development/execute/trigger`,
        { method: 'POST', body: JSON.stringify({ artefactId: artefact.id }) },
      );
      setExecuteSessionId(result.sessionId);
    } catch (err) {
      setExecuteError(err instanceof Error ? err.message : 'Failed to start execution');
    } finally {
      setExecuting(false);
    }
  };

  const handleConfirmGenerate = () => {
    setShowReworkConfirm(false);
    onGenerateCode();
  };

  return (
    <div
      style={{
        // Round 1 review (#848 HIGH): the `border` shorthand resets all
        // four sides, so the left-side longhand overrides MUST come
        // after it. Previously a redundant `borderLeft` shorthand sat
        // before `border`, then `border` clobbered it back to 1px and
        // only the colour stuck — making the amber NEEDS_REWORK accent
        // render at 1px instead of the intended 3px.
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeftStyle: 'solid',
        borderLeftWidth: 3,
        borderLeftColor: needsRework ? 'rgb(245, 158, 11)' : 'var(--primary)',
        padding: 16,
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>&#128196;</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Implementation Prompt
          </div>
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text)',
            }}
          >
            {artefact.title}
          </div>
        </div>
        <StatusBadge needsRework={needsRework} />
      </div>

      {/* Markdown preview */}
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          color: 'var(--text-secondary)',
          background: 'var(--surface-alt)',
          borderRadius: 'var(--radius-sm)',
          padding: '10px 12px',
          whiteSpace: 'pre-wrap',
          maxHeight: expanded ? 'none' : 72,
          overflow: 'hidden',
          marginBottom: 8,
        }}
      >
        {expanded ? artefact.content : previewLines}
      </div>

      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          color: 'var(--primary)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          marginBottom: 12,
          fontWeight: 500,
        }}
      >
        {expanded ? 'Collapse' : 'Expand'}
      </button>

      {/* GH.A2 (#942) — feature branch surface (post-approval) */}
      {artefactStatus === 'approved' && branchRef && (
        <div
          data-testid="feature-branch-badge"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 10,
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--success)',
          }}
        >
          <span aria-hidden="true">🌿</span>
          <span>Branch: {branchRef}</span>
        </div>
      )}
      {/* #1037 — bridge the post-approve gap: approved but the branch poll is
          still in flight (first ~50s). Without this, the Approve button vanishes
          and nothing appears until branchRef lands, which reads like the action
          silently failed. */}
      {artefactStatus === 'approved' && !branchRef && !branchPollExhausted && (
        <div
          data-testid="feature-branch-pending"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            background: 'rgba(16, 185, 129, 0.06)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--success)',
          }}
        >
          <Spinner className="w-3 h-3 inline" />
          <span>Approved — creating feature branch…</span>
        </div>
      )}
      {artefactStatus === 'approved' && !branchRef && branchPollExhausted && (
        <div
          data-testid="feature-branch-retry"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '6px 10px',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'rgb(180, 83, 9)',
          }}
        >
          <span>
            ⚠ Branch not created (no GitHub repo configured or creation failed). Retry once GitHub provisioning completes.
          </span>
          <button
            onClick={() => void handleRetryBranch()}
            disabled={retrying}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: 12,
              border: '1px solid rgb(245, 158, 11)',
              background: 'transparent',
              color: 'rgb(180, 83, 9)',
              cursor: retrying ? 'not-allowed' : 'pointer',
              opacity: retrying ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      {branchError && (
        <div
          data-testid="feature-branch-error"
          style={{
            padding: '4px 8px',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--error)',
          }}
        >
          {branchError}
        </div>
      )}

      {/* CRA.A2 (#946) — PR surface (post-branch). Only meaningful once a
          branch exists; legacy/no-branch artefacts show no PR section. */}
      {artefactStatus === 'approved' && branchRef && prUrl && (
        <div
          data-testid="pull-request-badge"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            background: 'rgba(124, 58, 237, 0.08)',
            border: '1px solid rgba(124, 58, 237, 0.3)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 10,
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--primary)',
          }}
        >
          <span aria-hidden="true">🔀</span>
          <a
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--primary)', textDecoration: 'underline' }}
          >
            {prNumber ? `Pull request #${prNumber}` : 'View pull request'}
          </a>
          <span style={{ color: 'var(--text-secondary)' }}>· Code review pending</span>
        </div>
      )}
      {artefactStatus === 'approved' && branchRef && !prUrl && !prPollExhausted && (
        <div
          data-testid="pull-request-pending"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <Spinner className="w-3 h-3 inline" /> Opening pull request…
        </div>
      )}
      {artefactStatus === 'approved' && branchRef && !prUrl && prPollExhausted && (
        <div
          data-testid="pull-request-retry"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '6px 10px',
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            color: 'rgb(180, 83, 9)',
          }}
        >
          <span>⚠ Pull request not created. Retry, or pick an existing PR from the review session.</span>
          <button
            onClick={() => void handleRetryPr()}
            disabled={retryingPr}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: 12,
              border: '1px solid rgb(245, 158, 11)',
              background: 'transparent',
              color: 'rgb(180, 83, 9)',
              cursor: retryingPr ? 'not-allowed' : 'pointer',
              opacity: retryingPr ? 0.6 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {retryingPr ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      {prError && (
        <div
          data-testid="pull-request-error"
          style={{
            padding: '4px 8px',
            marginBottom: 10,
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--error)',
          }}
        >
          {prError}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {/* #1037 — Approve is the primary workflow-advance action: it fires
            GH.A2 (branch) + CRA.A2 (PR). Shown until approved; intentionally
            NOT gated by NEEDS_REWORK (that verdict gates the Phase 4→5 handoff,
            not this transition — every prompt is NEEDS_REWORK today per #1036). */}
        {artefactStatus !== 'approved' && (
          <button
            onClick={() => void handleApprove()}
            disabled={approving}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--primary)',
              color: 'white',
              cursor: approving ? 'not-allowed' : 'pointer',
              opacity: approving ? 0.6 : 1,
            }}
          >
            {approving ? <><Spinner className="w-3 h-3 inline mr-1" /> Approving…</> : 'Approve →'}
          </button>
        )}
        {/* #1037 — Generate Code is now the secondary "send prompt to Claude
            Code" action (outline styling). It does not advance the workflow. */}
        <button
          onClick={handleGenerateClick}
          disabled={generating}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text)',
            cursor: generating ? 'not-allowed' : 'pointer',
            opacity: generating ? 0.6 : 1,
          }}
        >
          {generating ? <><Spinner className="w-3 h-3 inline mr-1" /> Generating...</> : 'Generate Code →'}
        </button>
        {/* AUTO.1 Part 3 — manual Execute: a thin caller of the same
            execution entry the auto-trigger uses. Shown once approved
            (mirrors Generate Code's availability); the server, not this
            button, decides whether execution is actually armed. */}
        {artefactStatus === 'approved' && (
          <button
            data-testid="execute-button"
            onClick={() => void handleExecute()}
            disabled={executing || !!executeSessionId}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 500,
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text)',
              cursor: executing || executeSessionId ? 'not-allowed' : 'pointer',
              opacity: executing || executeSessionId ? 0.6 : 1,
            }}
          >
            {executing
              ? <><Spinner className="w-3 h-3 inline mr-1" /> Starting execution…</>
              : executeSessionId ? 'Execution started' : 'Execute →'}
          </button>
        )}
        <button
          onClick={handleExport}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 14px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            background: 'transparent',
            color: 'var(--text)',
            cursor: 'pointer',
          }}
        >
          Export as Markdown
        </button>
        <button
          onClick={() => onRequestChanges('Please update the implementation prompt: ')}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 12,
            fontWeight: 500,
            padding: '6px 14px',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            background: 'transparent',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Request Changes
        </button>
      </div>

      {executeError && (
        <div
          data-testid="execute-error"
          style={{
            padding: '4px 8px',
            marginTop: 8,
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--error)',
          }}
        >
          {executeError}
        </div>
      )}

      {approveError && (
        <div
          data-testid="approve-error"
          style={{
            padding: '4px 8px',
            marginTop: 8,
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--error)',
          }}
        >
          {approveError}
        </div>
      )}

      {showReworkConfirm && reflection && (
        <ReworkConfirmModal
          reflection={reflection}
          onCancel={() => setShowReworkConfirm(false)}
          onConfirm={handleConfirmGenerate}
        />
      )}
    </div>
  );
}

function StatusBadge({ needsRework }: { needsRework: boolean }) {
  if (needsRework) {
    return (
      <span
        data-testid="implementation-prompt-status-badge"
        data-status="needs-rework"
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 12,
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.45)',
          color: 'rgb(180, 83, 9)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Needs rework &mdash; review self-assessment before generating
      </span>
    );
  }
  return (
    <span
      data-testid="implementation-prompt-status-badge"
      data-status="ready"
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 12,
        background: 'rgba(124, 58, 237, 0.10)',
        border: '1px solid rgba(124, 58, 237, 0.35)',
        color: 'var(--primary)',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      Ready to generate
    </span>
  );
}

interface ReworkConfirmModalProps {
  reflection: ImplementationPromptReflectionGate;
  onCancel: () => void;
  onConfirm: () => void;
}

function ReworkConfirmModal({ reflection, onCancel, onConfirm }: ReworkConfirmModalProps) {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const hasNoFlaggedItems =
    reflection.shortcutsIdentified.length === 0 && reflection.scopeAdditions.length === 0;

  // Round 1 review (#848 BLOCKER): Escape-key closes + initial focus.
  // Mirrors the codebase modal convention used by InviteModal,
  // SlideOver, TierFeaturesPopover, SessionHistoryDrawer.
  useEffect(() => {
    cancelButtonRef.current?.focus();
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  return (
    <div
      data-testid="rework-confirm-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rework-confirm-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md, 8px)',
          padding: 20,
          maxWidth: 560,
          width: 'calc(100% - 32px)',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
        }}
      >
        <h3
          id="rework-confirm-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--text)',
          }}
        >
          The Dev Agent flagged this prompt for rework
        </h3>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            color: 'var(--text-secondary)',
            marginTop: 8,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          The self-review identified items that should be addressed before generating code. The Phase 4 exit gate will block transition until these are resolved. You can still generate code, but the issues below will surface again at phase handoff.
        </p>

        {reflection.shortcutsIdentified.length > 0 && (
          <section data-testid="rework-confirm-shortcuts" style={{ marginBottom: 12 }}>
            <h4
              style={{
                margin: 0,
                marginBottom: 6,
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Shortcuts identified
            </h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {reflection.shortcutsIdentified.map((s) => (
                <li
                  key={s}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    color: 'var(--text)',
                    lineHeight: 1.5,
                    marginBottom: 4,
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>
          </section>
        )}

        {reflection.scopeAdditions.length > 0 && (
          <section data-testid="rework-confirm-scope" style={{ marginBottom: 12 }}>
            <h4
              style={{
                margin: 0,
                marginBottom: 6,
                fontFamily: 'var(--font-body)',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              Scope additions
            </h4>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {reflection.scopeAdditions.map((s) => (
                <li
                  key={s}
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 13,
                    color: 'var(--text)',
                    lineHeight: 1.5,
                    marginBottom: 4,
                  }}
                >
                  {s}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Round 1 review (#848 MEDIUM): defensive fallback for the
            "did not converge" edge case where the agent emits NEEDS_REWORK
            with both lists empty. Without this, the modal body would be
            blank below the heading paragraph. */}
        {hasNoFlaggedItems && (
          <p
            data-testid="rework-confirm-no-items"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              marginTop: 0,
              marginBottom: 12,
              lineHeight: 1.5,
              fontStyle: 'italic',
            }}
          >
            The Dev Agent flagged this prompt for rework but did not list specific shortcuts or scope additions. Review the implementation prompt against the original acceptance criteria before generating code.
          </p>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.25)';
              e.currentTarget.style.borderColor = 'var(--primary)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 500,
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--text)',
              cursor: 'pointer',
              outline: '2px solid transparent',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.25)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'rgb(245, 158, 11)',
              color: 'white',
              cursor: 'pointer',
              outline: '2px solid transparent',
            }}
          >
            Generate anyway
          </button>
        </div>
      </div>
    </div>
  );
}
